import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Print from '../../images/print.svg';
import Pdf from '../../images/pdf.svg';
import CSV from '../../images/csv.svg';
import Excel from '../../images/exl.svg';
import Send from '../../images/newDesign/send.svg';
import ExtractIcon from '../../images/exctract_icon.svg';
import { hideLoader, showLoader } from '../../store/common/slice';
import { getBuildingTitleText } from '../../store/buildings/actions';
import { createPdfFromHtml } from '../shared/statelessMethods';
import { exportToExcel } from '../utils/exportToExcel';
import TableToExcel from '../utils/tableToExcel';
import BllinkUtils from '../utils/utils';
import PdfWrapper from './PdfWrapperNew';
import { StyledPrintPdfDropdown } from './PrintPdfDropdown.styled';
import { PdfIframe } from './PdfIframe';
import { getReportRows } from "./ReportData";
import { SendReportModal } from "./SendReportModal";
import { useAccessToken } from "../hooks/useAccessToken";
import { fileToBase64 } from "../../../utils/fileToBase64";
import { isAdminSelector } from "../../store/auth/selectors";
import { NotificationManager } from "react-notifications";

/**
 * @typedef {enum} ColumnType
 * 
 * @property {'Numbers'} string - column type, for now it can be set only to "Number" - extend if needed here and in exportToExcel.js
 */

/**
 * @param {string} className
 * @param {string} buildingID
 * @param {string} partnerID
 * @param {string} name
 * @param {string} titleString
 * @param {string} reportName
 * @param {string} excelTitle
 * @param {ReactElement} pdfContainer
 * @param {ReportData} rows
 * @param {Object[]} titleRow
 * @param {function} onClick
 * @param {string} note
 * @param {string[]} ignoreColumns
 * @param {boolean} table
 * @param {boolean} pdfIframe
 * @param {PdfSettings} pdfSettings
 * @param {XlsSettings} xlsSettings
 * @param {boolean} disablePdf
 */
function PrintPdfDropdown({
  className,
  buildingID,
  partnerID,
  partnerName,
  reportName,
  titleString,
  excelTitle,
  pdfContainer,
  rows,
  titleRow,
  onClick,
  note,
  ignoreColumns,
  table,
  address,
  pdfIframe,
  disablePdf,
  pdfSettings,
  xlsSettings,
}) {
    const client = useAccessToken();
    const isAdmin = useSelector(isAdminSelector);
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [isPdfAvailable, setIsPdfAvailable] = useState(false);
    const [emailData, setEmailData] = useState({});
    const [showDropdown, setShowDropdown] = useState(false);
    const [createPdf, setCreatePdf] = useState(false);
    const [disablePdfSave, setDisablePdfSave] = useState(false);
    const [isSendReportModalOpen, setIsSendReportModalOpen] = useState(false);
    const titleData = useSelector(state => state.buildings.titleData);

    const ref = useRef(null);

    useEffect(() => {
        const onOutsideClick = e => ref.current.contains(e.target) ? setShowDropdown(true) : setShowDropdown(false);
        document.addEventListener('click', onOutsideClick);
        return () => document.removeEventListener('click', onOutsideClick);
    }, []);

    const reportSubjectData = useMemo(() => {
        return `${reportName || ''} ${titleData?.translationKey ? t(`payments.${titleData.translationKey}`, titleData): ''}`.trim();
    }, [titleData, reportName])

    /**
     * @param fileType
     * @param {boolean} disableFileSave
     * @return {Promise<void | Blob>}
     */
    const onSaveFileClick = async (fileType, disableFileSave = false) => {
        let title = '';
        if (buildingID) {
            const titleData = await dispatch(getBuildingTitleText({t, buildingID}));
            title = titleData.payload || '';
        }
        const fileName = `${title} - ${excelTitle}`;
        if (table) {
            //  in this case we parse table data to excel (Balance, Yearly by months reports)
            return TableToExcel.exportToExcel2(fileName, fileType, disableFileSave);
        } else {
            // in this case we pass parsed data and it's converted to excel (all reports without tables)
            dispatch(showLoader());
            const data = await getReportRows(rows, 'xls');
            dispatch(hideLoader());
            if (data != null) {
                const rtl = i18n.language === 'he';
                return exportToExcel(fileName, fileType, disableFileSave, data, titleRow, ignoreColumns, xlsSettings?.columnTypes, rtl);
            }
        }
    };

    const onSaveCSVClick = async e => {
        setShowDropdown(false);
        e.preventDefault();
        await onSaveFileClick('csv');
    };

    const onSaveExcelClick = async e => {
        setShowDropdown(false);
        e.preventDefault();
        await onSaveFileClick('excel');
    };

    const onSavePdfClick = () => {
        // new pdf infra
        if (pdfIframe) {
            setCreatePdf(true)
        } else {
            if (onClick != null) onClick();
            window.setTimeout(() => setIsPdfAvailable(true), 100);
        }
    };

    const getFileName = async () => {
        let title = '';
        if (buildingID) {
            const titleData = await dispatch(getBuildingTitleText({t, buildingID}));
            title = titleData.payload;
        }

        const currentDate = BllinkUtils.BllinkTime.parseDate(Date.now()).split('/').join('_');
        return `${currentDate}-${titleString} - ${title}`;
    }
    
    const onSavePdf = async container => {
        dispatch(showLoader());
        const title = await getFileName();
        const pdfBlob = await createPdfFromHtml(container,`${title}.pdf`, {save: !disablePdfSave});
        if (disablePdfSave) {
            await sendingBlobFileByEmail(pdfBlob);
        }
        dispatch(hideLoader());
        setIsPdfAvailable(false);
        setShowDropdown(false);
    };

    const onPrintClick = () => {
        if (onClick != null) onClick();
        setShowDropdown(false);
        window.setTimeout(() => window.print(), 100);
    };

    const onSendReportClick = (e) => {
        e.preventDefault();
        setIsSendReportModalOpen(true);
    }

    const sendingBlobFileByEmail = useCallback(async (pdfBlob, extension = 'pdf') => {
        const file = await fileToBase64(pdfBlob);
        setDisablePdfSave(false);
        const fileSimpleName = await getFileName();
        const fileName = `${fileSimpleName}.${extension}`;
        if (emailData?.email) {
            const result = await client.tenants.utils.sendReportByEmail(
                file,
                emailData.email,
                fileName,
                reportSubjectData,
                'application/pdf',
                emailData
            );
            if (result.sent) {
                NotificationManager.success(t('adminReports.reportSent'));
            }
        }
    }, [disablePdfSave, setIsSendReportModalOpen]);

    const handlePostingDataDone = () => setCreatePdf(false);

    /**
     * @param {SelectionData} data
     * @return Promise<void>
     */
    const handleSendingDataSet = async (data) => {
        if (data) {
            setEmailData({
                ...data,
                reportName,
            });
            setDisablePdfSave(true);
            if (disablePdf) {
                const fileBlob = await onSaveFileClick('excel', true);
                if (fileBlob) {
                    await sendingBlobFileByEmail(fileBlob, 'xlsx');
                }
            } else {
                onSavePdfClick();
            }
        }
    };

    const showSendReportOption = useMemo(() => {
        return buildingID && isAdmin && (excelTitle || !disablePdf)
    }, [buildingID, isAdmin, excelTitle, disablePdf])

    return (
        <StyledPrintPdfDropdown 
            ref={ref}
            className={`icons-bar ${className ? className : ''}`}
        >
            <img src={ExtractIcon} />
            {showDropdown && (
                <div className='dropdown'>
                    {!disablePdf && (
                        <div className='icons-section' onClick={onSavePdfClick}>
                            <img className="managerIcon" src={Pdf} alt="pdf" /> 
                            {t('reports.pdf')}
                        </div>
                    )}
                
                    {excelTitle && (
                        <>
                            <div className='icons-section' onClick={onSaveExcelClick}>
                                <img className="managerIcon" src={Excel} alt="csv" /> 
                                {t('reports.excel')}
                            </div>
                            <div className='icons-section' onClick={onSaveCSVClick}>
                                <img className="managerIcon" src={CSV} alt="csv" /> 
                                {t('reports.csv')}
                            </div>                      
                        </>
                    )}
                    <div className='icons-section' onClick={onPrintClick}>
                        <img className="managerIcon" src={Print} alt="print" /> 
                        {t('reports.print')}
                    </div>
                    {showSendReportOption &&
                        <div className='icons-section' onClick={onSendReportClick}>
                            <img className="managerIcon" src={Send} alt="send" />
                            {t('reports.send')}
                        </div>
                    }
                </div>
            )}
            {
                isPdfAvailable && (
                    <PdfWrapper
                        container={pdfContainer}
                        onSavePdf={onSavePdf}
                        note={note}
                    />
                )
            }
            <PdfIframe
                reportData={rows}
                disabled={!createPdf}
                disablePdfSave={disablePdfSave}
                onPostingDataDone={handlePostingDataDone}
                onPdfGenerated={sendingBlobFileByEmail}
                titleRow={titleRow}
                partnerName={partnerName}
                partnerID={partnerID}
                buildingAddress={address}
                reportTitle={titleString}
                pdfSettings={pdfSettings}
            />
            <SendReportModal
                buildingID={buildingID}
                show={isSendReportModalOpen}
                onClose={() => setIsSendReportModalOpen(false)}
                handleSelection={handleSendingDataSet}
            />
        </StyledPrintPdfDropdown>
    );
}

export default PrintPdfDropdown;
